import { useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import { useApartment, useUpdateApartment } from "@/api/districts";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Separator } from "@/components/ui/separator";
import {
	Sheet,
	SheetContent,
	SheetDescription,
	SheetFooter,
	SheetHeader,
	SheetTitle,
} from "@/components/ui/sheet";
import { Skeleton } from "@/components/ui/skeleton";
import { useHideElectricity } from "@/hooks/use-hide-electricity";
import { updateApartmentSchema } from "@/lib/schemas/district.schema";
import {
	AlertsSection,
	CurrentMetersSection,
	DetailRow,
	RatesSection,
} from "./apartment-detail-sections";
import { ApartmentEditForm } from "./apartment-edit-form";

type ApartmentDetailSheetProps = {
	apartmentId: number | null;
	onOpenChange: (open: boolean) => void;
	onRequestDelete: (apartmentId: number) => void;
};

export function ApartmentDetailSheet({
	apartmentId,
	onOpenChange,
	onRequestDelete,
}: ApartmentDetailSheetProps) {
	const { t } = useTranslation();
	const [editing, setEditing] = useState(false);
	const hideElectricity = useHideElectricity();
	const { data, isLoading } = useApartment(apartmentId ?? 0);

	const [street, setStreet] = useState("");
	const [apartmentNumber, setApartmentNumber] = useState("");
	const [coldWaterRate, setColdWaterRate] = useState("");
	const [hotWaterRate, setHotWaterRate] = useState("");
	const [heatRate, setHeatRate] = useState("");
	const [consumptionRate, setConsumptionRate] = useState("");

	const update = useUpdateApartment(apartmentId ?? 0);
	const prevDataId = useRef<number | null>(null);

	const open = apartmentId !== null;

	if (data && data.id !== prevDataId.current) {
		prevDataId.current = data.id;
		setStreet(data.street);
		setApartmentNumber(data.apartment_number);
		setColdWaterRate(data.cold_water_rate?.toString() ?? "");
		setHotWaterRate(data.hot_water_rate?.toString() ?? "");
		setHeatRate(data.heat_rate?.toString() ?? "");
		setConsumptionRate(data.consumption_rate?.toString() ?? "");
	}

	function handleSave() {
		const result = updateApartmentSchema.safeParse({
			street,
			apartmentNumber,
			coldWaterRate,
			hotWaterRate,
			heatRate,
			consumptionRate,
		});
		if (!result.success) {
			toast.error(
				result.error.issues[0]?.message ?? t("common.validationError"),
			);
			return;
		}
		update.mutate(
			{
				street: street.trim(),
				apartment_number: apartmentNumber.trim(),
				cold_water_rate: coldWaterRate ? Number(coldWaterRate) : null,
				hot_water_rate: hotWaterRate ? Number(hotWaterRate) : null,
				heat_rate: heatRate ? Number(heatRate) : null,
				consumption_rate: consumptionRate ? Number(consumptionRate) : null,
			},
			{
				onSuccess: () => {
					toast.success(t("apartments.updated"));
					setEditing(false);
				},
				onError: () => {
					toast.error(t("apartments.updateError"));
				},
			},
		);
	}

	const canSave = street.trim() && apartmentNumber.trim() && !update.isPending;

	return (
		<Sheet
			open={open}
			onOpenChange={(o) => {
				if (!o) {
					setEditing(false);
				}
				onOpenChange(o);
			}}
		>
			<SheetContent>
				<SheetHeader>
					<SheetTitle>
						{isLoading ? (
							<Skeleton className="h-5 w-40" />
						) : (
							`${data?.street} ${data?.apartment_number}`
						)}
					</SheetTitle>
					<SheetDescription>Szczegóły lokalu</SheetDescription>
				</SheetHeader>

				<div className="flex flex-col gap-4 px-4 pb-4">
					{isLoading ? (
						Array.from({ length: 6 }).map((_, i) => (
							<Skeleton key={i} className="h-4 w-full" />
						))
					) : !editing ? (
						<>
							<DetailRow label="Ulica" value={data?.street ?? "—"} />
							<DetailRow
								label="Numer lokalu"
								value={data?.apartment_number ?? "—"}
							/>
							<DetailRow
								label="Lokator"
								value={
									data?.user ? (
										<Badge>Przypisany</Badge>
									) : (
										<Badge variant="outline">Brak lokatora</Badge>
									)
								}
							/>
							{data?.alerts && data.alerts.length > 0 && (
								<>
									<Separator />
									<AlertsSection alerts={data.alerts} />
								</>
							)}
							{data?.current_meters && (
								<>
									<Separator />
									<CurrentMetersSection
										meters={data.current_meters}
										hideElectricity={hideElectricity}
									/>
								</>
							)}
							<Separator />
							{data && (
								<RatesSection
									apartment={data}
									hideElectricity={hideElectricity}
								/>
							)}
						</>
					) : (
						<ApartmentEditForm
							street={street}
							onStreetChange={setStreet}
							apartmentNumber={apartmentNumber}
							onApartmentNumberChange={setApartmentNumber}
							coldWaterRate={coldWaterRate}
							onColdWaterRateChange={setColdWaterRate}
							hotWaterRate={hotWaterRate}
							onHotWaterRateChange={setHotWaterRate}
							heatRate={heatRate}
							onHeatRateChange={setHeatRate}
							consumptionRate={consumptionRate}
							onConsumptionRateChange={setConsumptionRate}
							hideElectricity={hideElectricity}
						/>
					)}
				</div>

				<SheetFooter>
					{!editing ? (
						<>
							<Button
								variant="destructive"
								onClick={() => {
									if (apartmentId !== null) {
										onRequestDelete(apartmentId);
									}
								}}
								disabled={isLoading}
							>
								Usuń
							</Button>
							<Button onClick={() => setEditing(true)} disabled={isLoading}>
								Edytuj
							</Button>
						</>
					) : (
						<>
							<Button
								variant="outline"
								onClick={() => setEditing(false)}
								disabled={update.isPending}
							>
								Anuluj
							</Button>
							<Button onClick={handleSave} disabled={!canSave}>
								{update.isPending ? "Zapisywanie…" : "Zapisz"}
							</Button>
						</>
					)}
				</SheetFooter>
			</SheetContent>
		</Sheet>
	);
}
